import {
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReuseModal } from "../../ReuseModal";
import { CustomButton } from "../../CustomButton";
import { ReuseTooltip } from "../../ReuseTooltip";
import { useParams } from "react-router-dom";
import { UserThatShareCampaign } from "../../../../types/apiTypes";
import { useCallback, useState } from "react";
import { SharedUser } from "./SharedUser";
import { SharedUserRow } from "./SharedUserRow";
import {
  useLazyGetUserByEmailQuery,
  useUpdateUsersPermissionsMutation,
} from "../../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { EMAIL_REGEX } from "../../../../utils/regex";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  sharedCampaignUsers: UserThatShareCampaign[];
  userEmail: string;
};

export const ShareCampaignModal = ({
  isOpen,
  setIsOpen,
  sharedCampaignUsers,
  userEmail,
}: Props) => {
  const { campaign_id } = useParams();

  const [sharedUsers, setSharedUsers] =
    useState<UserThatShareCampaign[]>(sharedCampaignUsers);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isHide, setIsHideModal] = useState<boolean>(false);

  const [getUserByEmail, { isLoading: isSearchingUser }] =
    useLazyGetUserByEmailQuery();

  const [updateUsersPermissions, { isLoading }] =
    useUpdateUsersPermissionsMutation();

  const searchUserHandler = (value: string) => {
    setSearchValue(value);
  };

  const inviteHandler = () => {
    if (!searchValue || !EMAIL_REGEX.test(searchValue)) {
      toast.error(
        <CustomToast
          type="error"
          text={"Please enter a valid email address"}
          title="Failed to Invite User"
        />,
      );

      return;
    }

    if (
      searchValue === userEmail ||
      sharedUsers.some(({ email }) => email === searchValue)
    ) {
      toast.error(
        <CustomToast
          type="error"
          text={"The user has already been invited"}
          title="Failed to Invite User"
        />,
      );

      return;
    }

    if (!searchValue || !EMAIL_REGEX.test(searchValue)) {
      toast.error(
        <CustomToast
          type="error"
          text={"Please enter a valid email address"}
          title="Failed to Invite User"
        />,
      );

      return;
    }

    getUserByEmail(searchValue)
      .unwrap()
      .then((user) => {
        setSharedUsers((prev) => [...prev, user]);

        setSearchValue("");
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title="Failed to Invite User"
          />,
        );
      });
  };

  const isInviteLimit = sharedUsers.length >= 10;

  const removeUserHandler = useCallback((id: string) => {
    setSharedUsers((prev) => prev.filter(({ user_id }) => user_id !== id));
  }, []);

  const changeUserPermissionsHandler = useCallback(
    (updatedUser: UserThatShareCampaign) => {
      setSharedUsers((prev) =>
        prev.map((user) =>
          user.user_id === updatedUser.user_id ? updatedUser : user,
        ),
      );
    },
    [],
  );

  const getChangedUsers = (): UserThatShareCampaign[] => {
    const updatedUsers: UserThatShareCampaign[] = [];

    sharedUsers.forEach((user) => {
      const sameUser = sharedCampaignUsers.find(
        (existUser) => existUser.user_id === user.user_id,
      );

      if (sameUser && JSON.stringify(sameUser) === JSON.stringify(user)) return;
      updatedUsers.push(user);
    });

    return updatedUsers;
  };

  const changedUsers = getChangedUsers();

  const submitHandler = () => {
    if (!campaign_id) return;
    updateUsersPermissions({ campaign_id, users: sharedUsers })
      .unwrap()
      .then(() => {
        console.log("[ShareCampaign] success");
        changedUsers.forEach(() => {});
        setIsOpen(false);
      })
      .catch((err) => {
        console.log("[ShareCampaign] error", err);
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title={"Failed to Share Campaign"}
          />,
        );
      });
  };

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Share this campaign"
      maxWidth="570px"
      submitBtnText="Save"
      submitBtnColor="#1C3C6C"
      onSubmit={submitHandler}
      isLoading={isLoading}
      isDisabled={
        isLoading ||
        (changedUsers.length === 0 &&
          sharedUsers.length === sharedCampaignUsers.length)
      }
      isDisabledCancelBtn={isLoading}
      isHide={isHide}
    >
      <Stack gap="16px">
        <Stack gap="24px">
          <Typography component="span" variant="body1" color="text.secondary">
            Invite a team member to collaborate on this campaign. You can update
            their permissions at any time.
          </Typography>
          <Stack flexDirection="row" gap="8px" width="100%">
            <TextField
              variant="outlined"
              value={searchValue}
              placeholder="Enter email address"
              sx={{ width: "100%" }}
              onChange={(e) => {
                searchUserHandler(e.target.value);
              }}
              disabled={isSearchingUser}
            />

            <ReuseTooltip
              text={
                isInviteLimit
                  ? "You’ve reached the maximum limit of 10 users."
                  : ""
              }
              hideChildren={false}
              maxWidth="200px"
            >
              <CustomButton
                onClick={inviteHandler}
                variant="contained"
                customColor="#1C3C6C"
                type="button"
                startIcon={
                  isSearchingUser && (
                    <CircularProgress size={24} sx={{ color: "inherit" }} />
                  )
                }
                disabled={isSearchingUser || isInviteLimit || !searchValue}
                sx={{ height: "40px" }}
              >
                {isInviteLimit
                  ? "Limit reached"
                  : isSearchingUser
                    ? "Invite"
                    : "Invite User"}
              </CustomButton>
            </ReuseTooltip>
          </Stack>
          <Box>
            <SharedUserRow
              nameBlock={
                <Typography variant="button" sx={{ color: "#232323" }}>
                  Name
                </Typography>
              }
              roleBlock={
                <Typography variant="button" sx={{ color: "#232323" }}>
                  Role
                </Typography>
              }
              permissionBlock={
                <Typography variant="button" sx={{ color: "#232323" }}>
                  Permissions
                </Typography>
              }
              menuBlock={<Box />}
              withBg={true}
            />
            <Stack
              sx={{
                overflowY: "auto",
                flexGrow: 1,
                maxHeight: "348px",
              }}
            >
              {sharedUsers.map((user) => (
                <SharedUser
                  key={user.user_id}
                  user={user}
                  isOwner={userEmail === user.email}
                  setHideModal={setIsHideModal}
                  changeUserPermissionsHandler={changeUserPermissionsHandler}
                  removeUserHandler={removeUserHandler}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
